import {
  DateRange,
  FrequencyUnit,
  HabitSchedule,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  Day,
  differenceInCalendarDays,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  Interval,
  isBefore,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { weekdaysNumberMap } from '../constants';
import { getScheduleFrequency } from '../get-schedule-frequency';
import { getScheduleInterval } from '../get-schedule-interval';
import { isBetween } from '../lib/date-fns';
import { Frequency, RRule } from '../lib/rrule';
import { getActualScheduleStartDate } from './get-actual-schedule-start-date';
import { getScheduleEndDate } from './get-schedule-end-date';
import { memoizeBySchedule } from './memoize-by-schedule';

export const getFirstOccurrenceFrom = (
  schedule: HabitSchedule,
  from: Timestamp,
) =>
  new RRule({
    ...RRule.fromString(schedule.rrule.format).origOptions,
    dtstart: startOfDay(schedule.startDate),
  }).after(from, true);

const computeFirstOccurrence = (schedule: HabitSchedule) => {
  if (schedule.frequency?.count) {
    return schedule.startDate;
  }

  return getFirstOccurrenceFrom(schedule, startOfDay(schedule.startDate));
};

const getFirstOccurrence = memoizeBySchedule(computeFirstOccurrence);

type WeekOptions = { weekStartsOn: Day };

type TimeframeUnit = {
  startOf: (date: Timestamp, options: WeekOptions) => Timestamp;
  endOf: (date: Timestamp, options: WeekOptions) => Timestamp;
  add: (date: Timestamp, amount: number) => Timestamp;
  difference: (later: Timestamp, earlier: Timestamp) => number;
  each: (interval: Interval, options: WeekOptions) => Timestamp[];
};

const dayTimeframeUnit: TimeframeUnit = {
  startOf: startOfDay,
  endOf: endOfDay,
  add: addDays,
  difference: differenceInCalendarDays,
  each: (interval) => eachDayOfInterval(interval),
};

const timeframeUnitByFrequency: Record<FrequencyUnit, TimeframeUnit> = {
  [FrequencyUnit.Week]: {
    startOf: startOfWeek,
    endOf: endOfWeek,
    add: addWeeks,
    difference: differenceInWeeks,
    each: (interval, options) => eachWeekOfInterval(interval, options),
  },
  [FrequencyUnit.Month]: {
    startOf: startOfMonth,
    endOf: endOfMonth,
    add: addMonths,
    difference: differenceInMonths,
    each: (interval) => eachMonthOfInterval(interval),
  },
  [FrequencyUnit.Year]: {
    startOf: startOfYear,
    endOf: endOfYear,
    add: addYears,
    difference: differenceInYears,
    each: (interval) => eachYearOfInterval(interval),
  },
};

// "every N days" plans per N days; every other daily rule keeps the week grid, as does a unit this build does not know
const getTimeframeUnit = (schedule: HabitSchedule) =>
  getScheduleInterval(schedule) > 1 &&
  RRule.fromString(schedule.rrule.format).options.freq === Frequency.DAILY
    ? dayTimeframeUnit
    : timeframeUnitByFrequency[getScheduleFrequency(schedule)] ??
      timeframeUnitByFrequency[FrequencyUnit.Week];

const clampToScheduleStart = (
  timeframeEnd: Timestamp,
  scheduleStartDate: Timestamp,
) =>
  new Date(
    Math.min(
      ...(isBefore(timeframeEnd, scheduleStartDate)
        ? [endOfDay(addDays(scheduleStartDate, -1)).getTime()]
        : []),
      timeframeEnd.getTime(),
    ),
  );

// the grid period holding `date`, before the schedule's own endDate clamps it
const getScheduleTimeframeGrid = (
  schedule: HabitSchedule,
  date: Timestamp,
  weekStartsOn: WeekDays,
): DateRange => {
  const unit = getTimeframeUnit(schedule);
  const options = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
  const scheduleStartDate = startOfDay(schedule.startDate);
  const scheduleStart = isBefore(date, schedule.startDate)
    ? getActualScheduleStartDate(schedule)
    : scheduleStartDate;
  const interval = getScheduleInterval(schedule);
  // get the first occurrence of the schedule to have the correct calculations of the timeframe
  const firstOccurrenceAfterScheduleStart = getFirstOccurrence(schedule);
  const firstOccurrence = isBefore(date, schedule.startDate)
    ? scheduleStart
    : firstOccurrenceAfterScheduleStart ?? scheduleStart;

  const dateStart = unit.startOf(date, options);
  // take the interval off the first occurrence's period, so that period closes the first timeframe
  const firstTimeframeStart = unit.add(
    unit.startOf(firstOccurrence, options),
    -(interval - 1),
  );
  // periods `date` sits into its interval; `(% + ) %` keeps it >= 0 for pre-origin dates
  const periodsIntoInterval =
    ((unit.difference(dateStart, firstTimeframeStart) % interval) + interval) %
    interval;
  // from dateStart, not add(origin, n): the far-past hop drifts over DST and snaps to the prior week, dropping due habits
  const dateIntervalStart = unit.add(dateStart, -periodsIntoInterval);
  const timeframeStart = new Date(
    Math.max(firstTimeframeStart.getTime(), dateIntervalStart.getTime()),
  );
  const timeframeEnd = unit.endOf(
    unit.add(timeframeStart, interval - 1),
    options,
  );

  return {
    startDate: timeframeStart,
    endDate: clampToScheduleStart(timeframeEnd, scheduleStartDate),
  };
};

export const getScheduleTimeframeByDate = (
  schedule: HabitSchedule,
  date: Timestamp,
  weekStartsOn: WeekDays,
): DateRange => {
  const { startDate, endDate } = getScheduleTimeframeGrid(
    schedule,
    date,
    weekStartsOn,
  );
  const scheduleEnd = getScheduleEndDate(schedule);

  return {
    startDate,
    endDate: new Date(
      Math.min(
        ...(scheduleEnd ? [scheduleEnd.getTime()] : []),
        endDate.getTime(),
      ),
    ),
  };
};

// off the grid end, not the clamped end — the latter resolves back to itself
export const getNextScheduleTimeframe = (
  schedule: HabitSchedule,
  date: Timestamp,
  weekStartsOn: WeekDays,
): DateRange =>
  getScheduleTimeframeByDate(
    schedule,
    addDays(getScheduleTimeframeGrid(schedule, date, weekStartsOn).endDate, 1),
    weekStartsOn,
  );

export const getScheduleTimeframesByDateRange = (
  schedule: HabitSchedule,
  {
    startDate,
    endDate,
    weekStartsOn,
  }: { startDate: Timestamp; endDate: Timestamp; weekStartsOn: WeekDays },
): DateRange[] => {
  const periods = getTimeframeUnit(schedule).each(
    { start: startDate, end: endDate },
    { weekStartsOn: weekdaysNumberMap[weekStartsOn] },
  );
  // Periods ascend and timeframes never overlap, so only the newest range can contain `date`.
  return periods.reduce<DateRange[]>((acc, date) => {
    const latest = acc[acc.length - 1];
    if (latest && isBetween(date, latest.startDate, latest.endDate)) {
      return acc;
    }

    acc.push(getScheduleTimeframeByDate(schedule, date, weekStartsOn));
    return acc;
  }, []);
};
